/**
 * Time on the drawn map (specs/map-graph.md → "Time on the drawn map"): the
 * pure part of the timeline axis. When a card happened, which cards are in
 * view, which ticks a span gets at a width, where a date lands on the axis.
 * No DOM, no strings; the renderer formats labels via t() and Intl.
 *
 *   stated / due   a point (a coarse precision — month, quarter, year — is
 *                  the period it names)
 *   reported       an upper bound: a short range ending at the report date,
 *                  never a point, because the event happened by then, not then
 */
const DAY = 86400000;
export const AXIS_CONFIG = Object.freeze({
  reportedWindowDays: 92, // how far back a reported-only date's range reaches
  minSpanDays: 21,
  padFraction: 0.04,
  minTickPx: { week: 44, month: 56, quarter: 64, year: 64 },
});

const utc = (y, m = 0, d = 1) => Date.UTC(y, m, d);

/** A `when` → { start, end, point, basis } in ms (UTC), or null. */
export function whenInterval(when, config = AXIS_CONFIG) {
  if (!when?.date) return null;
  const m = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?|-Q([1-4]))?$/.exec(String(when.date).trim());
  if (!m) return null;
  const y = Number(m[1]);
  let start;
  let end;
  if (m[4]) { start = utc(y, (Number(m[4]) - 1) * 3); end = utc(y, Number(m[4]) * 3); } else if (m[3]) { start = utc(y, Number(m[2]) - 1, Number(m[3])); end = start; } else if (m[2]) { start = utc(y, Number(m[2]) - 1); end = utc(y, Number(m[2])); } else { start = utc(y); end = utc(y + 1); }
  if (end > start) end -= DAY; // the period's last day, inclusive
  if (when.basis === 'reported') {
    return { start: end - config.reportedWindowDays * DAY, end, point: false, basis: 'reported' };
  }
  return { start, end, point: start === end, basis: when.basis || null };
}

/** Every dated card: id → interval. Undated cards are left out. */
export function cardTimes(graph, config = AXIS_CONFIG) {
  const out = new Map();
  (graph?.nodes || []).forEach((n) => {
    const iv = whenInterval(n.attrs?.when, config);
    if (iv) out.set(n.id, iv);
  });
  return out;
}

/** Counts per basis, for a summary of what the map knows about time. */
export function whenCounts(graph) {
  const c = { stated: 0, due: 0, reported: 0, none: 0 };
  (graph?.nodes || []).forEach((n) => {
    const b = n.attrs?.when?.date ? n.attrs.when.basis : 'none';
    c[b in c ? b : 'none'] += 1;
  });
  return c;
}

/**
 * Report rounds: each distinct report date (a document the map reads), with
 * its round number when the evidence gives one and how many cards it dates.
 */
export function reportRounds(graph) {
  const byDate = new Map();
  (graph?.nodes || []).forEach((n) => {
    const w = n.attrs?.when;
    if (w?.basis !== 'reported' || !w.date) return;
    const iv = whenInterval(w);
    if (!iv) return;
    const round = (n.claims || []).flatMap((c) => c.evidence || []).find((e) => e.date === w.date && e.round != null)?.round ?? null;
    const cur = byDate.get(iv.end) || { t: iv.end, round: null, count: 0 };
    cur.count += 1;
    if (cur.round == null && round != null) cur.round = round;
    byDate.set(iv.end, cur);
  });
  return [...byDate.values()].sort((a, b) => a.t - b.t);
}

/** The time span of the cards that intersect a map-space rect, padded; null when none is dated. */
export function spanInView(cards, times, rect, config = AXIS_CONFIG) {
  let lo = Infinity;
  let hi = -Infinity;
  cards.forEach((c) => {
    const iv = times.get(c.id);
    if (!iv) return;
    if (c.x + c.w < rect.x || c.x > rect.x + rect.w || c.y + c.h < rect.y || c.y > rect.y + rect.h) return;
    lo = Math.min(lo, iv.start);
    hi = Math.max(hi, iv.end);
  });
  if (lo === Infinity) return null;
  const min = config.minSpanDays * DAY;
  if (hi - lo < min) { const mid = (lo + hi) / 2; lo = mid - min / 2; hi = mid + min / 2; }
  const pad = (hi - lo) * config.padFraction;
  return { start: lo - pad, end: hi + pad };
}

/** The map-space rect a zoom transform shows on a width × height screen. */
export function viewRect(transform, width, height) {
  const k = transform?.k || 1;
  return { x: -(transform?.x || 0) / k, y: -(transform?.y || 0) / k, w: width / k, h: height / k };
}

const UNITS = ['week', 'month', 'quarter', 'year'];
const APPROX = { week: 7 * DAY, month: 30.44 * DAY, quarter: 91.31 * DAY, year: 365.25 * DAY };

/** The first boundary of `unit` at or after t, and the one after a boundary. */
function floorUnit(t, unit) {
  const d = new Date(t);
  const y = d.getUTCFullYear();
  if (unit === 'year') return utc(y);
  if (unit === 'quarter') return utc(y, Math.floor(d.getUTCMonth() / 3) * 3);
  if (unit === 'month') return utc(y, d.getUTCMonth());
  const day = utc(y, d.getUTCMonth(), d.getUTCDate());
  const dow = (new Date(day).getUTCDay() + 6) % 7; // Monday = 0
  return day - dow * DAY;
}
function nextUnit(t, unit) {
  const d = new Date(t);
  if (unit === 'week') return t + 7 * DAY;
  const step = { month: 1, quarter: 3, year: 12 }[unit];
  return utc(d.getUTCFullYear(), d.getUTCMonth() + step);
}

/** ISO week number of a date. */
export function isoWeek(t) {
  const d = new Date(t);
  const dayNum = d.getUTCDay() || 7;
  const thursday = utc(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 4 - dayNum);
  const yearStart = utc(new Date(thursday).getUTCFullYear(), 0, 1);
  return Math.ceil(((thursday - yearStart) / DAY + 1) / 7);
}

/**
 * Ticks for a span drawn `widthPx` wide: the finest unit (week → month →
 * quarter → year) whose ticks stay at least minTickPx apart. Each tick says
 * what it is; the renderer words it.
 */
export function chooseTicks(span, widthPx, config = AXIS_CONFIG) {
  if (!span || !(span.end > span.start) || !(widthPx > 0)) return { unit: null, ticks: [] };
  const pxPerMs = widthPx / (span.end - span.start);
  const unit = UNITS.find((u) => APPROX[u] * pxPerMs >= config.minTickPx[u]) || 'year';
  const ticks = [];
  for (let t = floorUnit(span.start, unit); t <= span.end && ticks.length < 400; t = nextUnit(t, unit)) {
    if (t < span.start) continue;
    const d = new Date(t);
    ticks.push({
      t,
      unit,
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      quarter: Math.floor(d.getUTCMonth() / 3) + 1,
      week: unit === 'week' ? isoWeek(t) : null,
      // the first tick of a new year carries the year as context
      newYear: unit !== 'year' && (ticks.length === 0 || ticks[ticks.length - 1].year !== d.getUTCFullYear()),
    });
  }
  return { unit, ticks };
}

/** Where a time lands on an axis `widthPx` wide showing `span`. */
export function timeToX(t, span, widthPx) {
  return ((t - span.start) / (span.end - span.start)) * widthPx;
}
